import { createContext, useContext, useEffect, useState } from "react";

const KEY = "aic-a11y";
const DEFAULTS = { textSize: 0, easyRead: false, contrast: false, readableFont: false, links: false, motion: false };
const Ctx = createContext({ prefs: DEFAULTS, set: () => {}, reset: () => {} });

// Persists ONLY non-sensitive display preferences (text size, easy read, contrast, font, links, motion).
// No personal data is stored; localStorage is used deliberately so settings survive between visits.
const load = () => {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) || "{}") };
  } catch (err) {
    console.warn("Accessibility preferences could not be read; using defaults.", err);
    return DEFAULTS;
  }
};

const save = (prefs) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch (err) {
    console.warn("Accessibility preferences could not be saved (storage unavailable).", err);
  }
};

const applyClasses = (prefs) => {
  const el = document.documentElement;
  el.classList.toggle("a11y-large", prefs.textSize === 1);
  el.classList.toggle("a11y-xlarge", prefs.textSize === 2);
  el.classList.toggle("a11y-easy", prefs.easyRead);
  el.classList.toggle("a11y-contrast", prefs.contrast);
  el.classList.toggle("a11y-font", prefs.readableFont);
  el.classList.toggle("a11y-links", prefs.links);
  el.classList.toggle("a11y-motion", prefs.motion);
};

export const A11yProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(load);

  useEffect(() => {
    applyClasses(prefs);
    save(prefs);
  }, [prefs]);

  const set = (k, v) => setPrefs((p) => ({ ...p, [k]: v }));
  const reset = () => setPrefs(DEFAULTS);
  return <Ctx.Provider value={{ prefs, set, reset }}>{children}</Ctx.Provider>;
};

export const useA11y = () => useContext(Ctx);
